import React, { useCallback, useEffect, useState } from "react";
import {
  WorkOrdersAPI, WorkOrderDto, WorkOrderStatus, STATUS_LABELS, STATUS_COLORS,
} from "../api/workOrders";
import CreateWorkOrderModal from "../components/WorkOrders/CreateWorkOrderModal";

interface Props {
  user: any;
}

const FILTERS: { key: "" | WorkOrderStatus; label: string }[] = [
  { key: "", label: "Все" },
  { key: "new", label: "Новые" },
  { key: "in_progress", label: "В работе" },
  { key: "done", label: "Выполнены" },
  { key: "cancelled", label: "Отменены" },
];

const fmt = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleString("ru-RU", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—";

export default function WorkOrdersPage({ user }: Props) {
  const managerial = ["ADMIN", "OFFICE_LEAD", "SENIOR_OFFICE"].includes(user?.role);
  const [scope, setScope] = useState<"mine" | "all">("mine");
  const [status, setStatus] = useState<"" | WorkOrderStatus>("");
  const [orders, setOrders] = useState<WorkOrderDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    try {
      setOrders(await WorkOrdersAPI.list({ scope, status: status || undefined }));
    } finally {
      setLoading(false);
    }
  }, [scope, status]);

  useEffect(() => {
    setLoading(true);
    load();
    const t = setInterval(load, 30_000);
    return () => clearInterval(t);
  }, [load]);

  const open = orders.find((o) => o.id === openId) ?? null;
  const active = orders.filter((o) => o.status === "new" || o.status === "in_progress").length;

  return (
    <div style={{ padding: 20, maxWidth: 1100, margin: "0 auto" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 14 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24 }}>Выполнение работ</h1>
          <div style={{ color: "#9ca3af", fontSize: 13, marginTop: 2 }}>
            {scope === "mine" ? "Наряды, назначенные на вас" : "Все заказ-наряды"} · в работе и новых: {active}
          </div>
        </div>
        <button style={primaryBtn} onClick={() => setCreating(true)}>+ Новый наряд</button>
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14, alignItems: "center" }}>
        {managerial && (
          <div style={{ display: "flex", gap: 0, marginRight: 8 }}>
            {(["mine", "all"] as const).map((s) => (
              <button key={s} onClick={() => setScope(s)} style={{ ...chip, borderRadius: s === "mine" ? "8px 0 0 8px" : "0 8px 8px 0", background: scope === s ? "#2563eb" : "transparent", color: scope === s ? "#fff" : "#9ca3af" }}>
                {s === "mine" ? "Мои" : "Все"}
              </button>
            ))}
          </div>
        )}
        {FILTERS.map((f) => (
          <button key={f.key} onClick={() => setStatus(f.key)} style={{ ...chip, background: status === f.key ? "#374151" : "transparent", color: status === f.key ? "#fff" : "#9ca3af" }}>
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ color: "#9ca3af", padding: 30 }}>Загрузка…</div>
      ) : orders.length === 0 ? (
        <div style={{ textAlign: "center", color: "#6b7280", padding: "50px 0" }}>
          <div style={{ fontSize: 42, marginBottom: 8 }}>🛠</div>
          {scope === "mine" ? "На вас пока нет нарядов" : "Нарядов нет"}
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 12 }}>
          {orders.map((o) => {
            const done = o.works.filter((w) => w.done).length;
            return (
              <div key={o.id} onClick={() => setOpenId(o.id)} style={{ ...card, borderLeft: `4px solid ${STATUS_COLORS[o.status]}` }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                  <b style={{ fontSize: 14 }}>{o.number}</b>
                  <span style={{ fontSize: 12, fontWeight: 600, color: STATUS_COLORS[o.status] }}>{STATUS_LABELS[o.status]}</span>
                </div>
                <div style={{ fontSize: 15, fontWeight: 600, margin: "6px 0 4px" }}>{o.title}</div>
                <div style={meta}>{o.kindLabel}{o.clientName ? ` · ${o.clientName}` : ""}</div>
                {o.address && <div style={meta}>📍 {o.address}</div>}
                <div style={meta}>🕒 {fmt(o.scheduledAt)}</div>
                {scope === "all" && <div style={meta}>👷 {o.assigneeName ?? "не назначен"}{(o.coAssigneeNames ?? []).length > 0 ? ` + ${o.coAssigneeNames.join(", ")}` : ""}</div>}
                {o.works.length > 0 && <div style={{ ...meta, marginTop: 6 }}>Работы: {done} из {o.works.length}</div>}
              </div>
            );
          })}
        </div>
      )}

      {open && (
        <WorkOrderDetail
          order={open}
          user={user}
          managerial={managerial}
          onClose={() => setOpenId(null)}
          onChanged={(o) => setOrders((list) => list.map((x) => (x.id === o.id ? o : x)))}
        />
      )}

      {creating && (
        <CreateWorkOrderModal
          onClose={() => setCreating(false)}
          onSaved={(o) => { setCreating(false); setScope(managerial ? "all" : scope); load(); setOpenId(o.id); }}
        />
      )}
    </div>
  );
}

function WorkOrderDetail({ order, user, managerial, onClose, onChanged }: {
  order: WorkOrderDto; user: any; managerial: boolean; onClose: () => void; onChanged: (o: WorkOrderDto) => void;
}) {
  const isAssignee = order.assigneeId === user?.id || (order.coAssigneeIds ?? []).map(Number).includes(user?.id);
  const canWork = managerial || isAssignee;
  const canEdit = managerial || order.createdById === user?.id;
  const closed = order.status === "done" || order.status === "cancelled";

  const [done, setDone] = useState<Set<number>>(new Set(order.works.map((w, i) => (w.done ? i : -1)).filter((i) => i >= 0)));
  const [note, setNote] = useState(order.resultNote ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(false);
  const [info, setInfo] = useState("");

  const run = async (fn: () => Promise<WorkOrderDto>) => {
    setBusy(true);
    setError("");
    try {
      onChanged(await fn());
    } catch (e: any) {
      setError(e?.response?.data?.message ?? "Не удалось выполнить действие");
    } finally {
      setBusy(false);
    }
  };

  const saveProgress = () => WorkOrdersAPI.progress(order.id, { done: Array.from(done), resultNote: note });

  const syncDeal = async () => {
    setBusy(true);
    setError("");
    setInfo("");
    try {
      // сначала сохраняем несохранённые отметки, чтобы они не потерялись
      if (canWork && order.status === "in_progress") await saveProgress();
      const r = await WorkOrdersAPI.syncFromDeal(order.id);
      onChanged(r.order);
      setDone(new Set(r.order.works.map((w, i) => (w.done ? i : -1)).filter((i) => i >= 0)));
      const parts: string[] = [];
      if (r.added.length) parts.push(`Добавлено: ${r.added.join("; ")}`);
      if (r.changed.length) parts.push(`Изменено количество: ${r.changed.join("; ")}`);
      if (r.notInDeal.length) parts.push(`Есть в наряде, но нет в сделке (оставлено): ${r.notInDeal.join("; ")}`);
      setInfo(parts.length ? parts.join("\n") : "Наряд уже совпадает со сделкой — изменений нет");
    } catch (e: any) {
      setError(e?.response?.data?.message ?? "Не удалось обновить из сделки");
    } finally {
      setBusy(false);
    }
  };

  const toggle = (i: number) => {
    if (!canWork || closed || order.status !== "in_progress") return;
    setDone((s) => { const n = new Set(s); n.has(i) ? n.delete(i) : n.add(i); return n; });
  };

  const finish = () => {
    if (done.size < order.works.length && !window.confirm(`Отмечено ${done.size} из ${order.works.length} работ. Всё равно завершить?`)) return;
    run(async () => { await saveProgress(); return WorkOrdersAPI.setStatus(order.id, "done", note); });
  };

  return (
    <div style={overlay} onClick={onClose}>
      <div style={modal} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
          <div>
            <div style={{ fontSize: 12, color: "#9ca3af" }}>{order.kindLabel}</div>
            <h2 style={{ margin: "2px 0 0", fontSize: 20 }}>{order.number}</h2>
          </div>
          <span style={{ fontWeight: 700, color: STATUS_COLORS[order.status] }}>{STATUS_LABELS[order.status]}</span>
        </div>
        <div style={{ fontSize: 16, fontWeight: 600, margin: "10px 0 8px" }}>{order.title}</div>

        <div style={infoGrid}>
          <Info k="Клиент" v={order.clientName} />
          <Info k="Телефон" v={order.contactPhone} link={order.contactPhone ? `tel:${order.contactPhone.replace(/[^\d+]/g, "")}` : undefined} />
          <Info k="Адрес" v={order.address} />
          <Info k="Исполнитель" v={order.assigneeName ?? "не назначен"} />
          {(order.coAssigneeNames ?? []).length > 0 && <Info k="Соисполнители" v={order.coAssigneeNames.join(", ")} />}
          <Info k="Плановая дата" v={fmt(order.scheduledAt)} />
          <Info k="Наряд выдал" v={order.createdByName} />
        </div>

        {order.description && <div style={descBox}>{order.description}</div>}

        <div style={sectionTitle}>Работы {order.works.length > 0 && `(${done.size}/${order.works.length})`}</div>
        {order.works.length === 0 ? <div style={meta}>Перечень работ не заполнен</div> : order.works.map((w, i) => (
          <label key={i} style={{ display: "flex", gap: 10, alignItems: "center", padding: "7px 0", borderBottom: "1px solid #1f2937", cursor: canWork && order.status === "in_progress" ? "pointer" : "default" }}>
            <input type="checkbox" checked={done.has(i)} onChange={() => toggle(i)} disabled={!canWork || order.status !== "in_progress"} style={{ width: 18, height: 18 }} />
            <span style={{ flex: 1, textDecoration: done.has(i) ? "line-through" : "none", color: done.has(i) ? "#6b7280" : "#e5e7eb" }}>{w.name}</span>
            <span style={meta}>{w.qty} {w.unit}</span>
          </label>
        ))}
        {order.status === "new" && canWork && order.works.length > 0 && <div style={{ ...meta, marginTop: 6 }}>Нажмите «Принять в работу», чтобы отмечать выполнение.</div>}

        {order.materials.length > 0 && (
          <>
            <div style={sectionTitle}>Оборудование и материалы</div>
            {order.materials.map((m, i) => (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "5px 0", borderBottom: "1px solid #1f2937", fontSize: 14 }}>
                <span>{m.name}</span><span style={meta}>{m.qty} {m.unit}</span>
              </div>
            ))}
          </>
        )}

        <div style={sectionTitle}>Результат / комментарий</div>
        <textarea
          style={{ ...descBox, width: "100%", boxSizing: "border-box", minHeight: 70, color: "#fff", resize: "vertical", fontFamily: "inherit" }}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          disabled={!canWork || closed}
          placeholder={canWork && !closed ? "Что сделано, замечания, что осталось…" : ""}
        />

        {error && <div style={{ color: "#f87171", fontSize: 13, marginTop: 10 }}>{error}</div>}
        {info && <div style={{ color: "#7ec8e3", fontSize: 13, marginTop: 10, whiteSpace: "pre-wrap", background: "rgba(126,200,227,0.08)", borderRadius: 8, padding: "8px 10px" }}>{info}</div>}

        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 16 }}>
          {order.status === "new" && canWork && (
            <button style={{ ...primaryBtn, background: "#f59e0b" }} disabled={busy} onClick={() => run(() => WorkOrdersAPI.setStatus(order.id, "in_progress"))}>▶ Принять в работу</button>
          )}
          {order.status === "in_progress" && canWork && (
            <>
              <button style={{ ...primaryBtn, background: "#10b981" }} disabled={busy} onClick={finish}>✓ Завершить</button>
              <button style={ghostBtn} disabled={busy} onClick={() => run(saveProgress)}>Сохранить отметки</button>
            </>
          )}
          {closed && managerial && (
            <button style={ghostBtn} disabled={busy} onClick={() => run(() => WorkOrdersAPI.setStatus(order.id, "in_progress"))}>↩ Вернуть в работу</button>
          )}
          <button style={ghostBtn} onClick={() => WorkOrdersAPI.openPdf(order.id)}>📄 PDF</button>
          {order.dealId && !closed && (canEdit || canWork) && (
            <button style={ghostBtn} disabled={busy} onClick={syncDeal} title="Подтянуть позиции, добавленные в сделку после создания наряда">↻ Обновить из сделки</button>
          )}
          {canEdit && !closed && <button style={ghostBtn} onClick={() => setEditing(true)}>✎ Изменить</button>}
          {canEdit && !closed && (
            <button style={{ ...ghostBtn, color: "#f87171" }} disabled={busy} onClick={() => window.confirm("Отменить наряд?") && run(() => WorkOrdersAPI.setStatus(order.id, "cancelled"))}>Отменить</button>
          )}
          <button style={{ ...ghostBtn, marginLeft: "auto" }} onClick={onClose}>Закрыть</button>
        </div>

        <div style={{ ...meta, marginTop: 12 }}>
          Создан {fmt(order.createdAt)}{order.startedAt ? ` · начат ${fmt(order.startedAt)}` : ""}{order.completedAt ? ` · завершён ${fmt(order.completedAt)}` : ""}
        </div>
      </div>

      {editing && (
        <CreateWorkOrderModal order={order} onClose={() => setEditing(false)} onSaved={(o) => { setEditing(false); onChanged(o); }} />
      )}
    </div>
  );
}

function Info({ k, v, link }: { k: string; v?: string | null; link?: string }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.4 }}>{k}</div>
      <div style={{ fontSize: 14 }}>{v ? (link ? <a href={link} style={{ color: "#7ec8e3" }}>{v}</a> : v) : "—"}</div>
    </div>
  );
}

const card: React.CSSProperties = { background: "#131926", border: "1px solid #1f2937", borderRadius: 12, padding: 14, cursor: "pointer" };
const meta: React.CSSProperties = { fontSize: 13, color: "#9ca3af" };
const chip: React.CSSProperties = { border: "1px solid #374151", padding: "7px 14px", fontSize: 13, cursor: "pointer", borderRadius: 8 };
const primaryBtn: React.CSSProperties = { background: "#2563eb", color: "#fff", border: "none", borderRadius: 8, padding: "10px 18px", fontSize: 14, fontWeight: 600, cursor: "pointer" };
const ghostBtn: React.CSSProperties = { background: "transparent", color: "#d1d5db", border: "1px solid #374151", borderRadius: 8, padding: "9px 14px", fontSize: 13, cursor: "pointer" };
const overlay: React.CSSProperties = { position: "fixed", inset: 0, zIndex: 9000, background: "rgba(0,0,0,0.65)", display: "flex", alignItems: "center", justifyContent: "center", padding: 12 };
const modal: React.CSSProperties = { width: "100%", maxWidth: 620, maxHeight: "92vh", overflowY: "auto", background: "#131926", border: "1px solid #1f2937", borderRadius: 14, padding: 22, color: "#e5e7eb" };
const infoGrid: React.CSSProperties = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 10 };
const descBox: React.CSSProperties = { background: "#1a2030", border: "1px solid #2a3344", borderRadius: 8, padding: "9px 11px", fontSize: 14, marginTop: 10, whiteSpace: "pre-wrap" };
const sectionTitle: React.CSSProperties = { fontSize: 13, fontWeight: 700, color: "#9ca3af", margin: "16px 0 6px", textTransform: "uppercase", letterSpacing: 0.4 };
